import { Helmet } from "react-helmet-async";
import { getCurrencySymbol } from "@/features/types";
import useGetDenseOrderSheetSet, {
  GetDenseOrderSheetSetPathParameter,
} from "@/features/order-sheet-sets/hooks/use-get-dense-order-sheet-set";
import useIdentity from "@/features/ui/hooks/use-identity";
import useLogOrderSheetView from "@/features/order-sheet-sets/hooks/use-log-order-sheet-view";
import useOrderSheetViewTip from "@/features/order-sheet-sets/hooks/use-order-sheet-view-tip";
import React, {
  startTransition,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import LineSheetNotFoundPage from "@/features/line-sheet-sets/line-sheet-not-found-page";
import { isLineSheetSetUnavailableError } from "@/features/line-sheet-sets/line-sheet-set-error";
import {
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spacer,
  Switch,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import AppIconButton from "@/features/line-sheet-sets/app-icon-button";
import { useParams } from "react-router";
import AppButton from "@/features/line-sheet-sets/app-button";
import {
  DenseOrderSheet,
  OrderSheetSet,
  OrderSheetStatus,
} from "@/features/order-sheets/order-sheet.type";
import { useRecoilState, useRecoilValue } from "recoil";
import OrderSheetSetSaveButton from "@/features/order-sheet-sets/order-sheet-set-save-button";
import { LightLineSheet } from "@/features/line-sheet-sets/line-sheet-set.types";
import { SubmitOrderSheetSetResponse } from "@/features/order-sheet-sets/hooks/use-submit-order-sheet-set";
import useAppDialog from "@/features/ui/hooks/use-app-dialog";
import { CreateOrderSheetRevisionResponse } from "@/features/line-sheet-sets/hooks/use-create-order-sheet-revision";
import OrderSheetOrderButton from "@/features/order-sheet-sets/order-sheet-set-order-button";
import { getOrderMoq } from "@/features/order-sheet-sets/moq-notice-banner";
import { canOrderByUnit } from "@/features/line-sheet-sets/helpers/can-order-by-unit";
import OrderSheetAdjustPriceFragment from "@/features/order-sheet-sets/order-sheet-adjust-price-fragment";
import SheetTabFilter, {
  EMPTY_SHEET_TAB_FILTER_QUERY,
  isProductFiltered,
  SheetTabFilterQuery,
  useStableSheetTabFilterQuery,
} from "@/features/order-sheet-sets/sheet-tab-filter";
import useOrderSheetSetPagePersistedQuery from "@/features/order-sheet-sets/hooks/use-order-sheet-set-page-persisted-query";
import useInflatedTabs from "@/features/line-sheet-sets/helpers/use-inflated-tabs";
import { ColumnApi, GridApi, GridReadyEvent } from "ag-grid-community";
import { produce } from "immer";
import {
  clientSheetStatisticFamily,
  FlatProduct,
  FlatSheetKey,
  inflatedTabsFamily,
  SheetStatisticState,
} from "@/features/line-sheet-sets/helpers/sheet-state";
import { UpdateLineSheetSetOrderRulesResponse } from "@/features/line-sheet-sets/hooks/use-update-line-sheet-set-order-rules";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import { useBeforeUnload } from "react-router-dom";
import { DEFAULT_LIST_REVISIONS_PAGE_SIZE } from "./hooks/use-list-order-sheet-revisions";
import { OrderSheetRevision } from "./order-sheet-revision.type";
import { CompanyType } from "../types";
import useListAllCompanies, {
  ListAllCompaniesPathParameter,
  ListAllCompaniesRequest,
} from "@/features/line-sheet-sets/hooks/use-list-all-companies";
import AppCheckBox from "@/features/line-sheet-sets/app-checkbox";
import SVG from "react-inlinesvg";
import CurrencySwitch from "@/shared/components/currency-switch";
import OrderSheetProductGallery, {
  DEFAULT_IMAGE_COUNT,
  IMAGE_COUNT_OPTIONS,
  ImageCount,
  ImageCountButton,
} from "@/features/order-sheet-sets/order-sheet-product-gallery";
import OrderSheetGallerySettingsModal from "@/features/order-sheet-sets/order-sheet-gallery-settings-modal";
import { isOrderSheetPageCurrencyKrw } from "@/features/order-sheet-sets/recoils/order-sheet-page-store";
import { BuyerOrderSheet } from "@/features/order-sheet-sets/buyer-order-sheet";
import useListOrderSheetRevisionsV2, {
  ListOrderSheetRevisionsV2PathParameter,
} from "./hooks/use-list-order-sheet-revisions-v2";
import useFetchAndSetOrderSheetTabsV2 from "./hooks/use-fetch-and-set-order-sheet-tabs-v2";
import useBulkUpdateOrderSheetSetStatus from "@/features/order-status/hooks/use-bulk-update-order-sheet-set-status";
import SheetInfoPanel from "@/features/line-sheet-sets/sheet-info-panel";
import AppOrderSheetStatusLabel from "@/features/ui/app-order-sheet-status-label";
import AppLabel from "@/features/ui/app-label";
import AppSelect from "@/features/line-sheet-sets/app-select";
import OrderStatusBadge from "@/features/order-status/OrderStatusBadge";
import AppDownloadButton from "@/features/invoices/app-download-button";
import OrderHistoryOrderInfoOrderCancelModal from "@/features/my-order/features/order-history/components/order-info-section/order-info-modals/OrderHistoryOrderInfoOrderCancelModal";
import OrderHistoryInquiryContent from "@/features/my-order/features/order-history/components/inquiry-section/OrderHistoryInquiryContent";
import InquiryUnreadBadge from "@/features/my-order/features/order-history/components/inquiry-section/InquiryUnreadBadge";
import CenteredSpinner from "@/shared/components/centered-spinner";
import useCreateManualExcelExport from "@/features/order-sheet-sets/hooks/use-create-manual-excel-export";
import useGetManualExcelExport from "@/features/order-sheet-sets/hooks/use-get-manual-excel-export";
import useDownloadUnoFile from "@/hooks/use-download-uno-file";
import { SubmitOrderSheetSetV2Response } from "@/features/order-sheet-sets/hooks/use-submit-order-sheet-set-v2";
import { getCanonicalUrl } from "@/shared/utils/canonical-url";
import useOrderSheetMarketPriceEnabled from "@/features/order-sheet-sets/hooks/use-order-sheet-market-price-enabled";
import { PathPickProductCardComparisonLayout } from "@/features/pathpick/types";
import useLineSheetSetMarketPrices from "@/features/order-sheet-sets/hooks/use-line-sheet-set-market-prices";

// 툴바에 늘어놓는 on/off — 라벨 + 스위치 한 쌍이 반복돼서 묶어 둔다.
function ToolbarSwitch({
  id,
  label,
  isChecked,
  onChange,
}: {
  id: string;
  label: string;
  isChecked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <FormControl display={"flex"} alignItems={"center"} width={"fit-content"}>
      <FormLabel htmlFor={id} mb={"0"} mr={"6px"}>
        <Text textStyle={"body-sm-regular"} whiteSpace={"nowrap"}>
          {label}
        </Text>
      </FormLabel>
      <Switch
        id={id}
        isChecked={isChecked}
        onChange={(event) => onChange(event.target.checked)}
      />
    </FormControl>
  );
}

// 뷰 선택은 브라우저에 남겨 다음 시트에서도 같은 화면으로 연다.
const ORDER_SHEET_VIEW_KEY = "orderSheetView";
const ORDER_SHEET_IMAGE_COUNT_KEY = "orderSheetImageCount";
const ORDER_SHEET_PRICE_DETAIL_KEY = "orderSheetShowPriceDetail";
const ORDER_SHEET_EXPAND_OPTIONS_KEY = "orderSheetExpandOptions";
const ORDER_SHEET_MARKET_LAYOUT_KEY = "orderSheetMarketComparisonLayout";

// 뷰 전환 아이콘 버튼 — 아이콘 세트에 격자/목록 아이콘이 없어 인라인 SVG로 그린다.
function ViewModeButton({
  isActive,
  label,
  onClick,
  children,
}: {
  isActive: boolean;
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Flex
      as={"button"}
      alignItems={"center"}
      justifyContent={"center"}
      boxSize={"28px"}
      borderRadius={"4px"}
      border={isActive ? "0.5px solid #1272EF" : "0.5px solid #D4D4D1"}
      background={isActive ? "#F0F9FF" : "#FCFCFB"}
      color={isActive ? "#1272EF" : "#8F8F8C"}
      _hover={{ borderColor: "#1272EF" }}
      aria-label={label}
      title={label}
      onClick={onClick}
    >
      <svg width={16} height={16} viewBox="0 0 24 24" fill="currentColor">
        {children}
      </svg>
    </Flex>
  );
}

export function isDenseOrderSheet(
  value: DenseOrderSheet | LightLineSheet
): value is DenseOrderSheet {
  return (value as LightLineSheet).name === undefined;
}

function BuyerOrderSheetSetPage() {
  const identity = useIdentity();
  const company = useMemo(() => {
    return identity?.company;
  }, [identity]);
  const { orderSheetSetId: rawOrderSheetSetId } = useParams();
  const [orderSheetSetStatus, setOrderSheetSetStatus] = useState<
    OrderSheetStatus | undefined
  >(undefined);
  const orderSheetSetId = useMemo(() => {
    let candidate;
    if (rawOrderSheetSetId) {
      candidate = parseInt(rawOrderSheetSetId);
    }
    return candidate || -1;
  }, [rawOrderSheetSetId]);
  const { t, tTitle, tSentence } = useI18nHelper();
  const [queryCount, setQueryCount] = useState(0);
  const isSheetInfoOpen = localStorage.getItem("isSheetInfoOpen") === "true";

  const { navigate } = useRouterHelper();
  const { error: showError } = useAppToasts();
  const { openDialog } = useAppDialog();
  const gridRef = useRef<{
    api: GridApi<FlatProduct>;
    columnApi: ColumnApi;
  } | null>(null);

  const handleGridReady = useCallback((event: GridReadyEvent<FlatProduct>) => {
    gridRef.current = {
      api: event.api,
      columnApi: event.columnApi,
    };
  }, []);

  const getDenseOrderSheetSetParameter = useMemo(():
    | GetDenseOrderSheetSetPathParameter
    | undefined => {
    if (company && orderSheetSetId > -1) {
      return {
        by: company.type,
        companyId: company.id,
        orderSheetSetId,
      };
    }
  }, [company, orderSheetSetId]);

  const {
    isLoading: isGetDenseOrderSheetSetLoading,
    isValidating: isGetDenseOrderSheetSetValidating,
    error: getDenseOrderSheetSetError,
    data: getDenseOrderSheetSetData,
    mutate: mutateGetDenseOrderSheetSet,
  } = useGetDenseOrderSheetSet(
    getDenseOrderSheetSetParameter,
    {},
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
      revalidateOnMount: true,
      dedupingInterval: 0,
      // 삭제/미발행 라인시트로 인한 합성 404는 안내 페이지로 처리하므로 전역 onError 토스트를 억제한다.
      // (그 외 에러는 기존과 동일하게 토스트로 알린다)
      onError: (err) => {
        if (!isLineSheetSetUnavailableError(err)) {
          showError(err);
        }
      },
    }
  );

  // 크림·포이즌 시세 비교 — 바이어 features 에 가격비교(ORDER_SHEET_MARKET_PRICE)가 있을 때만.
  // 시세는 라인시트 상품에 붙는 값이라 라인시트세트 단위로 한 번 받아 그리드와 카드 보기가 같은 값을 쓴다.
  const isMarketPriceEligible = useOrderSheetMarketPriceEnabled();
  const {
    isEnabled: isMarketPriceEnabled,
    byProductId: marketPriceByProductId,
    pricing: marketPricing,
  } =
    useLineSheetSetMarketPrices(
      company?.id,
      getDenseOrderSheetSetData?.lineSheetSet?.id,
      isMarketPriceEligible
    );

  useEffect(() => {
    // 라인시트세트가 삭제/미발행이면 dense OSS 패처가 lineSheetSet 을 못 붙여 합성 404를 던진다.
    // 이 경우는 아래에서 안내 페이지로 렌더하므로 토스트를 띄우지 않는다. (그 외 에러는 기존대로 토스트)
    if (
      getDenseOrderSheetSetError &&
      !isLineSheetSetUnavailableError(getDenseOrderSheetSetError)
    ) {
      showError(getDenseOrderSheetSetError);
    }
  }, [showError, getDenseOrderSheetSetError]);

  //order-sheet-set-select -->
  const { value: query, set: setQuery } = useOrderSheetSetPagePersistedQuery();

  // 상품 필터/정렬에 실제로 쓰이는 필드만 담은 안정적인 객체.
  // referer, revision_number 등 무관한 URL 변경으로 전 상품 재계산이 도는 것을 막는다.
  const sheetTabFilterQuery = useStableSheetTabFilterQuery(query);

  const currency = useMemo(() => {
    return getDenseOrderSheetSetData?.lineSheetSet.currency;
  }, [getDenseOrderSheetSetData]);

  //--> order-sheet-set-select

  /* order-sheet-revision-selector */

  const getOrderSheetRevisionsParameter =
    useMemo((): ListOrderSheetRevisionsV2PathParameter => {
      if (
        identity &&
        identity.company &&
        orderSheetSetId > -1 &&
        getDenseOrderSheetSetData
      ) {
        return {
          orderSheetSetId: orderSheetSetId,
          by: identity.company.type,
          companyId: identity.company.id,
        };
      }
      return {
        orderSheetSetId: -1,
        by: "BUYER" as CompanyType,
        companyId: -1,
      };
    }, [orderSheetSetId, getDenseOrderSheetSetData, identity]);

  const {
    isLoading: isGetOrderSheetRevisionsLoading,
    isValidating: isGetOrderSheetRevisionsValidating,
    error: getOrderSheetRevisionsError,
    data: getOrderSheetRevisionsData,
    mutate: mutateGetOrderSheetRevisions,
  } = useListOrderSheetRevisionsV2(
    getOrderSheetRevisionsParameter,
    {
      pageNumber: 1,
      pageSize: DEFAULT_LIST_REVISIONS_PAGE_SIZE,
    },
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
      revalidateOnMount: true,
      dedupingInterval: 0,
    }
  );

  const latestRevision = useMemo(() => {
    if (
      getOrderSheetRevisionsData &&
      getOrderSheetRevisionsData.revisions &&
      getOrderSheetRevisionsData.revisions.length > 0
    ) {
      return getOrderSheetRevisionsData.revisions.reduce(
        (prev: OrderSheetRevision, current: OrderSheetRevision) => {
          return prev.revisionNumber > current.revisionNumber ? prev : current;
        }
      );
    }
    return {
      revisionNumber: -1,
      createdAt: new Date(),
      createdBy: {
        id: -1,
        name: "",
        email: "",
      },
      createdByCompany: {
        id: -1,
        name: "",
        type: "BUYER" as CompanyType,
      },
    };
  }, [getOrderSheetRevisionsData]);

  const brandKey = useMemo(():
    | {
        parameter: ListAllCompaniesPathParameter;
        request: ListAllCompaniesRequest;
      }
    | undefined => {
    if (identity?.company) {
      let request: ListAllCompaniesRequest = {};
      request.type__in = ["BRAND"];
      return {
        parameter: {
          by: identity.company.type,
          companyId: identity.company.id,
        },
        request,
      };
    }
    return undefined;
  }, [identity]);

  const { data: companiesData } = useListAllCompanies(
    brandKey?.parameter,
    brandKey?.request
  );

  // made_by_company.id__in -> brand_name__eq 정규화가 이번 커밋에 실행될 예정인지.
  // 같은 커밋에서 setQuery를 두 번 호출하면 react-router의 setSearchParams가
  // 렌더 시점 스냅샷을 기준으로 계산하므로 뒤쪽 호출이 앞쪽 결과를 통째로 덮어쓴다.
  // (목록 -> 주문서 재진입처럼 SWR 캐시가 전부 히트일 때 브랜드 필터가 사라지던 원인)
  const isBrandFilterNormalizationPending =
    (query["madeByCompany.id__in"]?.length ?? 0) > 0 && !!companiesData;

  useEffect(() => {
    const madeByCompanyIds = query["madeByCompany.id__in"];
    if (madeByCompanyIds && madeByCompanyIds.length > 0 && companiesData) {
      setQuery(
        (prev) => {
          return {
            ...prev,
            "madeByCompany.id__in": undefined,
            brandName__eq: madeByCompanyIds
              .map(
                (id) => companiesData.find((company) => company.id === id)?.name
              )
              .filter((company): company is string => company !== undefined),
          };
        },
        {
          replace: true,
        }
      );
    }
  }, [query, companiesData, setQuery]);

  useEffect(() => {
    if (!getDenseOrderSheetSetData) return;
    setOrderSheetSetStatus(getDenseOrderSheetSetData.status);
  }, [getDenseOrderSheetSetData]);

  const selectedOrderSheetRevision = useMemo(() => {
    if (
      getOrderSheetRevisionsData &&
      getOrderSheetRevisionsData.revisions.length > 0 &&
      typeof query.revisionNumber === "number" &&
      query.revisionNumber > 0
    ) {
      const revision = getOrderSheetRevisionsData.revisions.find(
        (revision) => revision.revisionNumber === query.revisionNumber
      );
      if (revision) {
        return revision;
      }
    }
    return latestRevision;
  }, [getOrderSheetRevisionsData, query.revisionNumber, latestRevision]);

  // 예전에는 여기서 "기본값 채워주기"로 revision_number / *_tab_index를 URL에 박았다.
  // buyer 페이지는 셋 다 읽는 곳이 없고(탭 인덱스는 sheetKey가 orderSheetId:-1 고정,
  // filterInflatedProducts는 tabIndex 0 리터럴), revisionNumber도 읽는 쪽이 이미
  // latestRevision으로 폴백한다. 반면 가드가 영원히 참이라 deps가 바뀔 때마다 재발화하며
  // navigate를 계속 쏘고, 공유받은 ?revision_number=N 딥링크를 최신 리비전으로 덮어썼다.
  // 제거해서 mount 시 URL 재작성을 한 번 줄인다.

  //--> order-sheet-revision-selector

  const { filterInflatedProducts, resetInflatedSheet } = useInflatedTabs();

  const sheetKey = useMemo((): FlatSheetKey => {
    if (getDenseOrderSheetSetData) {
      if (getOrderSheetRevisionsData) {
        return {
          orderSheetSetId: getDenseOrderSheetSetData.id,
          orderSheetId: -1,
          revisionNumber: selectedOrderSheetRevision.revisionNumber,
        };
      } else {
        return {
          orderSheetSetId: getDenseOrderSheetSetData.id,
          orderSheetId: -1,
          revisionNumber: -1,
        };
      }
    }

    return {
      orderSheetSetId: -1,
      revisionNumber: -1,
    };
  }, [
    getDenseOrderSheetSetData,
    getOrderSheetRevisionsData,
    selectedOrderSheetRevision,
  ]);

  const [isOrderSheetEditing, setIsOrderSheetEditing] =
    useState<boolean>(false);

  const [inflatedTabs, setInflatedTabs] = useRecoilState(
    inflatedTabsFamily(sheetKey)
  );

  const sheetStatisticState = useRecoilValue(
    clientSheetStatisticFamily(sheetKey)
  );

  const [initialSheetStatistics, setInitialSheetStatistics] =
    useState<SheetStatisticState>();

  useEffect(() => {
    if (initialSheetStatistics === undefined && sheetStatisticState) {
      setInitialSheetStatistics(sheetStatisticState);
    }
  }, [initialSheetStatistics, sheetStatisticState]);

  const isOrderSheetDirty = useMemo(() => {
    if (inflatedTabs) {
      const dirty = inflatedTabs.tabs.find((i) => i.isDirty) !== undefined;
      return dirty;
    }
    return false;
  }, [inflatedTabs]);

  const totalOrderQuantity =
    inflatedTabs?.tabs[0]?.products?.reduce((acc, product) => {
      const orderQuantityWithOptionList =
        product.orderQuantityWithOptionList || [];
      const productTotalQuantity = orderQuantityWithOptionList.reduce(
        (sum, option) => sum + (option.quantity || 0),
        0
      );
      return acc + productTotalQuantity;
    }, 0) || 0;

  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (isOrderSheetEditing || isOrderSheetDirty) {
        event.preventDefault();
        event.returnValue = "";
      }
    },
    [isOrderSheetEditing, isOrderSheetDirty]
  );

  useBeforeUnload(handleBeforeUnload);

  const confirmDiscardChanges = useCallback(() => {
    if (isOrderSheetEditing || isOrderSheetDirty) {
      return openDialog({
        title: tTitle("common:discard_changes"),
        message: t("common:discard_changes.message"),
        confirmName: tTitle("continue"),
      });
    }
    return Promise.resolve(true);
  }, [isOrderSheetDirty, isOrderSheetEditing, openDialog, t, tTitle]);

  const useFetchAndSetOrderSheetTabsParameter = useMemo(() => {
    if (
      getDenseOrderSheetSetData &&
      getOrderSheetRevisionsData &&
      selectedOrderSheetRevision
    ) {
      const orderSheetSet = getDenseOrderSheetSetData;
      return {
        data: {
          buyerCompanyId: orderSheetSet.createdByCompany.id,
          orderSheetSetId: getDenseOrderSheetSetData.id,
          revisionNumber: selectedOrderSheetRevision.revisionNumber,
          latestRevisionNumber: latestRevision.revisionNumber,
          lineSheetSetId: orderSheetSet.lineSheetSet.id,
          orderSheetSetStatus: orderSheetSet.status,
        },
        query: sheetTabFilterQuery,
      };
    }
    return {
      data: undefined,
    };
  }, [
    getDenseOrderSheetSetData,
    getOrderSheetRevisionsData,
    selectedOrderSheetRevision,
    latestRevision,
    sheetTabFilterQuery,
  ]);

  const { setInflatedTabsClean } = useFetchAndSetOrderSheetTabsV2(
    useFetchAndSetOrderSheetTabsParameter
  );

  const [isOrderSheetSaving, setIsOrderSheetSaving] = useState<boolean>(false);

  const [
    isUpdatingInflatedProductCompleted,
    setIsUpdatingInflatedProductCompleted,
  ] = useState(false);

  const orderSheetSetSaveButtonRef = useRef<{
    handleSaveOrderSheetSet: () => void;
  }>(null);

  const [
    isOrderSheetSetSaveButtonClicked,
    setIsOrderSheetSetSaveButtonClicked,
  ] = useState<boolean>(false);

  const orderSheetOrderButtonRef = useRef<{
    handleOrderOrderSheet: () => void;
  }>(null);

  const [isOrderSheetOrderButtonClicked, setIsOrderSheetOrderButtonClicked] =
    useState<boolean>(false);

  useEffect(() => {
    if (isUpdatingInflatedProductCompleted) {
      setIsUpdatingInflatedProductCompleted(false);

      /* 1. save 버튼을 클릭해서 호출된 경우 order-sheet-set-page.tsx에서 save 버튼이 클릭되었다는 상태값을 true로 변경
       * 2-1. save버튼이 클릭되었다는 상태값이 true인 경우 handleSaveOrderSheetSet()을 실행하고 상태값을 false로 변경
       * 2-2. save버튼이 클릭되었다는 상태값이 false인 경우 아무것도 하지 않음
       */
      if (
        orderSheetSetSaveButtonRef.current &&
        isOrderSheetSetSaveButtonClicked
      ) {
        orderSheetSetSaveButtonRef.current.handleSaveOrderSheetSet();
      }
      if (orderSheetOrderButtonRef.current && isOrderSheetOrderButtonClicked) {
        orderSheetOrderButtonRef.current.handleOrderOrderSheet();
      }
    }
  }, [
    isUpdatingInflatedProductCompleted,
    setIsUpdatingInflatedProductCompleted,
    orderSheetSetSaveButtonRef,
    isOrderSheetSetSaveButtonClicked,
    orderSheetOrderButtonRef,
    isOrderSheetOrderButtonClicked,
  ]);

  const handleAfterSaveOrderSheetSet = useCallback(
    async (response: CreateOrderSheetRevisionResponse) => {
      // 오더시트상태 업데이트 필요
      setOrderSheetSetStatus(response.orderSheetSet.status);
      setInflatedTabsClean();
      mutateGetDenseOrderSheetSet();
    },
    [setInflatedTabsClean, mutateGetDenseOrderSheetSet]
  );

  const handleOrderSheetSubmit = useCallback(
    (response: SubmitOrderSheetSetV2Response) => {
      setOrderSheetSetStatus(response.orderSheetSet.status);
      setInflatedTabsClean();
      mutateGetDenseOrderSheetSet();
      if (query.editable) {
        setQuery(
          (prev) => ({
            ...prev,
            editable: undefined,
          }),
          { replace: true }
        );
      }
    },
    [mutateGetDenseOrderSheetSet, query, setInflatedTabsClean, setQuery]
  );

  const isLoading =
    isGetDenseOrderSheetSetLoading ||
    isGetDenseOrderSheetSetValidating ||
    isOrderSheetSaving ||
    isGetOrderSheetRevisionsLoading;

  const isOrderSheetSetCancelable = useMemo(() => {
    if (orderSheetSetStatus) {
      return ["ORDERED_TO_AGENCY", "RESUBMIT_COMPLETED"].includes(
        orderSheetSetStatus
      );
    }
    return false;
  }, [orderSheetSetStatus]);

  const isOnEditEnabledPage = isOrderSheetSetCancelable && query.editable;

  const isOrderSheetSetEditable = useMemo(() => {
    if (company?.type === "BUYER") {
      if (
        selectedOrderSheetRevision &&
        latestRevision &&
        // 최신 리비전인 경우에만 편집 가능
        latestRevision.revisionNumber >
          selectedOrderSheetRevision.revisionNumber
      ) {
        return false;
      }
      if (isOnEditEnabledPage) {
        return true;
      }
      return ["INITIAL_DRAFT", "WORKING_DRAFT", "RESUBMIT_REQUIRED"].includes(
        orderSheetSetStatus || ""
      );
    }
    return false;
  }, [
    company?.type,
    orderSheetSetStatus,
    latestRevision,
    selectedOrderSheetRevision,
    isOnEditEnabledPage,
  ]);

  const isOrderSheetSetOrderable = useMemo(() => {
    if (orderSheetSetStatus) {
      return ["INITIAL_DRAFT", "WORKING_DRAFT", "RESUBMIT_REQUIRED"].includes(
        orderSheetSetStatus
      );
    }
    return false;
  }, [orderSheetSetStatus]);

  const isOrderSheetSetSavable = useMemo(() => {
    if (orderSheetSetStatus) {
      return ["INITIAL_DRAFT", "WORKING_DRAFT", "RESUBMIT_REQUIRED"].includes(
        orderSheetSetStatus
      );
    }
  }, [orderSheetSetStatus]);

  const isOrderSheetResetPossible = useMemo(() => {
    if (orderSheetSetStatus) {
      if (isOnEditEnabledPage) return true;
      return ["INITIAL_DRAFT", "WORKING_DRAFT", "RESUBMIT_REQUIRED"].includes(
        orderSheetSetStatus
      );
    }
  }, [orderSheetSetStatus, isOnEditEnabledPage]);

  const handleOrderSheetSetEditing = useCallback((isEditing: boolean) => {
    setIsOrderSheetEditing(isEditing);
  }, []);

  const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);

  const handleFilterSelect = useCallback(
    (query: SheetTabFilterQuery) => {
      setQuery(
        (prev) => {
          const next = {
            ...prev,
            ...query,
          };
          return next;
        },
        {
          replace: true,
        }
      );
    },
    [setQuery]
  );

  const handleSheetTabFilterClear = useCallback(() => {
    setQuery(
      (prev) => {
        return {
          ...prev,
          ...EMPTY_SHEET_TAB_FILTER_QUERY,
        };
      },
      {
        replace: true,
      }
    );
    // 관심 상품만 보기는 쿼리가 아니라 화면 상태지만, 사용자에겐 같은 '필터'다 — 같이 푼다.
    setLikedOnly(false);
  }, [setQuery]);

  useEffect(() => {
    if (!isNaN(selectedOrderSheetRevision.revisionNumber)) {
      filterInflatedProducts(
        sheetKey,
        0,
        (products) => {
          return isProductFiltered(products, sheetTabFilterQuery);
        },
        sheetTabFilterQuery.orderBy || "",
        isOrderSheetDirty
      );
    }
  }, [
    sheetKey,
    selectedOrderSheetRevision.revisionNumber,
    filterInflatedProducts,
    sheetTabFilterQuery,
    isOrderSheetDirty,
  ]);

  // 그리드(편집) / 갤러리(훑어보기) 전환. 갤러리는 읽기 전용이라 수량 입력은 그리드에서만 한다.
  // 고른 뷰는 시트를 옮겨도 유지한다 — 라인시트마다 다시 고르게 하지 않는다.
  const [isGalleryView, setIsGalleryView] = useState<boolean>(
    () => localStorage.getItem(ORDER_SHEET_VIEW_KEY) === "gallery"
  );
  const [imageCount, setImageCount] = useState<ImageCount>(() => {
    const saved = Number(localStorage.getItem(ORDER_SHEET_IMAGE_COUNT_KEY));
    // 저장값이 없거나 옵션에서 빠진 값이면 기본값으로 되돌린다.
    return (IMAGE_COUNT_OPTIONS as readonly number[]).includes(saved)
      ? (saved as ImageCount)
      : DEFAULT_IMAGE_COUNT;
  });

  useEffect(() => {
    localStorage.setItem(
      ORDER_SHEET_VIEW_KEY,
      isGalleryView ? "gallery" : "detail"
    );
  }, [isGalleryView]);

  // 바이어가 어떤 보기로 주문 화면을 쓰는지 서버에 남긴다(통계용) —
  // 주문서를 열 때 한 번, 버튼으로 보기를 바꿀 때마다. 고른 보기가 브라우저에만 남아 알 길이 없었다.
  const logOrderSheetViewSwitch = useLogOrderSheetView({
    buyerId: company?.type === "BUYER" ? company.id : undefined,
    orderSheetSetId,
    view: isGalleryView ? "CARD" : "DETAIL",
    isEnabled: getDenseOrderSheetSetData !== undefined,
  });
  // 보기를 한 번도 바꿔 본 적 없는 사용자에게 카드/자세히 보기 전환을 최초 1회 알린다.
  const { isOpen: isViewTipOpen, close: closeViewTip } = useOrderSheetViewTip({
    buyerId: company?.type === "BUYER" ? company.id : undefined,
    isEnabled: getDenseOrderSheetSetData !== undefined,
    isGalleryViewOnOpen: isGalleryView,
  });
  const handleViewChange = useCallback(
    (nextIsGalleryView: boolean) => {
      // 버튼을 눌렀다면 안내는 할 일을 다 했다
      closeViewTip();
      // 이미 켜져 있는 보기를 다시 누른 건 전환이 아니다
      if (nextIsGalleryView === isGalleryView) {
        return;
      }
      setIsGalleryView(nextIsGalleryView);
      logOrderSheetViewSwitch(nextIsGalleryView ? "CARD" : "DETAIL");
    },
    [isGalleryView, logOrderSheetViewSwitch, closeViewTip]
  );

  useEffect(() => {
    localStorage.setItem(ORDER_SHEET_IMAGE_COUNT_KEY, String(imageCount));
  }, [imageCount]);

  // 카드에 무엇을 보여줄지 — 톱니(설정)에서 고르고 시트를 옮겨도 유지한다.
  const [showPriceDetail, setShowPriceDetail] = useState<boolean>(
    () => localStorage.getItem(ORDER_SHEET_PRICE_DETAIL_KEY) === "true"
  );
  const [isAllExpanded, setIsAllExpanded] = useState<boolean>(
    () => localStorage.getItem(ORDER_SHEET_EXPAND_OPTIONS_KEY) === "true"
  );
  const {
    isOpen: isGallerySettingsOpen,
    onOpen: onGallerySettingsOpen,
    onClose: onGallerySettingsClose,
  } = useDisclosure();

  const handleShowPriceDetailChange = useCallback((value: boolean) => {
    setShowPriceDetail(value);
    localStorage.setItem(ORDER_SHEET_PRICE_DETAIL_KEY, String(value));
  }, []);

  // 크림·포이즌 비교 배치 — 패스픽 카드와 같은 두 가지(나란히/위아래), 기본도 패스픽과 같은 위아래.
  const [marketComparisonLayout, setMarketComparisonLayout] =
    useState<PathPickProductCardComparisonLayout>(() =>
      localStorage.getItem(ORDER_SHEET_MARKET_LAYOUT_KEY) === "side"
        ? "side"
        : "stack"
    );
  const handleMarketComparisonLayoutChange = useCallback(
    (layout: PathPickProductCardComparisonLayout) => {
      localStorage.setItem(ORDER_SHEET_MARKET_LAYOUT_KEY, layout);
      // 카드 폭과 줄 수가 같이 바뀌는 변경이라 설정 모달이 먼저 반응하게 transition 으로 돌린다.
      startTransition(() => setMarketComparisonLayout(layout));
    },
    []
  );

  // 카드가 많으면 옵션 표를 한꺼번에 그리느라 화면이 굳는다.
  // transition 으로 돌려 설정 모달은 먼저 반응하게 한다.
  const handleIsAllExpandedChange = useCallback((value: boolean) => {
    localStorage.setItem(ORDER_SHEET_EXPAND_OPTIONS_KEY, String(value));
    startTransition(() => setIsAllExpanded(value));
  }, []);
  // 별을 눌러 둔 상품만 보기 — 카드 보기·자세히 보기가 같은 값을 쓴다.
  const [likedOnly, setLikedOnly] = useState<boolean>(false);
  const [isOrderRulesPanelOpen, setIsOrderRulesPanelOpen] = useState<boolean>(
    localStorage.getItem("isSheetInfoOpen") === "true"
  );

  const handleOrderRulesToggle = useCallback(() => {
    setIsOrderRulesPanelOpen((prev) => !prev);
    localStorage.setItem("isSheetInfoOpen", String(!isOrderRulesPanelOpen));
  }, [isOrderRulesPanelOpen]);

  const handleOrderRulesSave = useCallback(
    (response: UpdateLineSheetSetOrderRulesResponse) => {
      mutateGetDenseOrderSheetSet(
        (prev) => {
          if (prev) {
            return produce(prev, (draft) => {
              draft.lineSheetSet.orderRules = response.orderRules;
            });
          }
        },
        { revalidate: false }
      );
    },
    [mutateGetDenseOrderSheetSet]
  );

  const isConfirmedEditable = false;

  const isConditionEditable = useMemo(() => {
    return (
      (isOrderSheetSetEditable &&
        orderSheetSetStatus !== "RESUBMIT_REQUIRED") ||
      isConfirmedEditable
    );
  }, [orderSheetSetStatus, isConfirmedEditable, isOrderSheetSetEditable]);

  const {
    isLoading: isBulkUpdateOrderSheetSetStatusLoading,
    data: bulkUpdateOrderSheetSetStatusData,
    error: bulkUpdateOrderSheetSetStatusError,
    fire: fireBulkUpdateOrderSheetSetStatus,
  } = useBulkUpdateOrderSheetSetStatus();

  useEffect(() => {
    if (bulkUpdateOrderSheetSetStatusError) {
      showError(bulkUpdateOrderSheetSetStatusError);
    }
    if (bulkUpdateOrderSheetSetStatusData) {
      // 오더 취소가 성공하면 다시 데이터를 가져와서 새로고침 처리를 함
      bulkUpdateOrderSheetSetStatusData.orderSheetSets.map((orderSheetSet) => {
        if (
          orderSheetSet.id === getDenseOrderSheetSetData?.id &&
          (orderSheetSet.status === "WORKING_DRAFT" ||
            orderSheetSet.status === "CANCELED")
        ) {
          mutateGetDenseOrderSheetSet();
        }
      });
    }
  }, [
    showError,
    mutateGetDenseOrderSheetSet,
    bulkUpdateOrderSheetSetStatusError,
    bulkUpdateOrderSheetSetStatusData,
    getDenseOrderSheetSetData?.id,
  ]);

  const [isKrw, setIsKrw] = useRecoilState(isOrderSheetPageCurrencyKrw);
  // 원화 라인시트세트는 통화 변환을 감춘다 — 다른 세트에서 켜 둔 원화 보기가 남아
  // 원화 가격을 다시 환산하지 않게 끈다
  useEffect(() => {
    if (currency === "KRW" && isKrw) {
      setIsKrw(false);
    }
  }, [currency, isKrw, setIsKrw]);

  const lineSheetSetName = getDenseOrderSheetSetData
    ? `#${getDenseOrderSheetSetData?.lineSheetSet.id} ${getDenseOrderSheetSetData?.lineSheetSet.name}`
    : "";

  const downloadFile = useMemo(() => {
    if (getDenseOrderSheetSetData) {
      if (
        getDenseOrderSheetSetData.status === "INITIAL_DRAFT" ||
        getDenseOrderSheetSetData.status === "WORKING_DRAFT"
      ) {
        return getDenseOrderSheetSetData?.lineSheetSet?.unoFormFile;
      } else {
        return getDenseOrderSheetSetData?.unoFormFile;
      }
    }
    return null;
  }, [getDenseOrderSheetSetData]);

  const {
    isLoading: isCreateManualExcelExportLoading,
    fire: fireCreateManualExcelExport,
  } = useCreateManualExcelExport();

  const { fire: fireGetManualExcelExport } = useGetManualExcelExport();

  const {
    fire: fireDownloadManualExcelExportFile,
    isLoading: isDownloadManualExcelExportFileLoading,
  } = useDownloadUnoFile();

  const [isManualExcelExportPolling, setIsManualExcelExportPolling] =
    useState(false);

  const fireGetManualExcelExportRef = useRef(fireGetManualExcelExport);
  const fireDownloadManualExcelExportFileRef = useRef(
    fireDownloadManualExcelExportFile
  );
  const showErrorRef = useRef(showError);

  useEffect(() => {
    fireGetManualExcelExportRef.current = fireGetManualExcelExport;
  }, [fireGetManualExcelExport]);
  useEffect(() => {
    fireDownloadManualExcelExportFileRef.current =
      fireDownloadManualExcelExportFile;
  }, [fireDownloadManualExcelExportFile]);
  useEffect(() => {
    showErrorRef.current = showError;
  }, [showError]);

  useEffect(() => {
    if (!isManualExcelExportPolling || !company) return;

    const parameter = { buyerId: company.id, orderSheetSetId };
    const intervalId = setInterval(async () => {
      try {
        const result = await fireGetManualExcelExportRef.current(parameter, {});
        if (result.status === "COMPLETED") {
          clearInterval(intervalId);
          setIsManualExcelExportPolling(false);
          if (result.unoFile) {
            fireDownloadManualExcelExportFileRef.current(
              { unoFileId: result.unoFile.id },
              {}
            );
          }
        } else if (result.status === "FAILED") {
          clearInterval(intervalId);
          setIsManualExcelExportPolling(false);
        }
      } catch (e: any) {
        clearInterval(intervalId);
        setIsManualExcelExportPolling(false);
        showErrorRef.current(e);
      }
    }, 3000);

    return () => {
      clearInterval(intervalId);
    };
  }, [isManualExcelExportPolling, company, orderSheetSetId]);

  const {
    isOpen: isCancelOpen,
    onOpen: onCancelOpen,
    onClose: onCancelClose,
  } = useDisclosure();
  // 문의/답변 — 주문내역 화면과 같은 채팅을 오른쪽 패널로 연다
  const {
    isOpen: isInquiryOpen,
    onOpen: onInquiryOpen,
    onClose: onInquiryClose,
  } = useDisclosure();

  const currentLssType = getDenseOrderSheetSetData?.lineSheetSet.type;

  useEffect(() => {
    if (query.referer) return;
    // 브랜드 필터 정규화와 같은 커밋에서 쓰면 이쪽이 정규화 결과(brand_name__eq)를
    // 덮어써 브랜드 필터가 유실된다. 정규화가 끝난 다음 커밋에서 쓴다.
    if (isBrandFilterNormalizationPending) return;
    setQuery((prev) => ({
      ...prev,
      "nav.type__eq": currentLssType,
    }));
  }, [query, setQuery, currentLssType, isBrandFilterNormalizationPending]);

  // 컨디션 적용은 결국 공급가를 고치는 기능이라, 자세히 보기에서는 그 컬럼 바로 위에 붙인다.
  // 시트정보 패널을 열면 그리드가 오른쪽으로 밀리므로 계산 대신 헤더 셀을 직접 잰다.
  // 단, 같은 줄의 왼쪽(시트정보·이미지 수)과 오른쪽(다운로드·정렬·보기 전환) 도구와 겹치면 안 되므로
  // 그 사이로 밀어 넣고, 사이가 모자라면 아랫줄로 내린다.
  const detailBodyRef = useRef<HTMLDivElement | null>(null);
  const toolbarLeftRef = useRef<HTMLDivElement | null>(null);
  const toolbarRightRef = useRef<HTMLDivElement | null>(null);
  const adjustPriceFragmentRef = useRef<HTMLDivElement | null>(null);
  const [adjustPriceLeft, setAdjustPriceLeft] = useState(0);
  const [isAdjustPriceOnOwnRow, setIsAdjustPriceOnOwnRow] = useState(false);

  useLayoutEffect(() => {
    if (isGalleryView || selectedProductIds.length === 0) return;
    const container = detailBodyRef.current;
    if (!container) return;
    const TOOLBAR_GAP = 8;
    const measure = () => {
      const header = container.querySelector(
        '.ag-header-cell[col-id="priceAmount"]'
      );
      if (!header) return;
      const containerLeft = container.getBoundingClientRect().left;
      const columnLeft = header.getBoundingClientRect().left - containerLeft;
      const fragmentWidth = adjustPriceFragmentRef.current?.offsetWidth ?? 0;
      const leftToolbar = toolbarLeftRef.current?.getBoundingClientRect();
      const rightToolbar = toolbarRightRef.current?.getBoundingClientRect();
      const minLeft = leftToolbar
        ? leftToolbar.right - containerLeft + TOOLBAR_GAP
        : 0;
      const maxLeft = rightToolbar
        ? rightToolbar.left - containerLeft - TOOLBAR_GAP - fragmentWidth
        : Number.POSITIVE_INFINITY;
      if (fragmentWidth > 0 && maxLeft < minLeft) {
        setIsAdjustPriceOnOwnRow(true);
        return;
      }
      setIsAdjustPriceOnOwnRow(false);
      setAdjustPriceLeft(Math.max(minLeft, Math.min(columnLeft, maxLeft)));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    // 공급가 왼쪽 칼럼을 접고 펴면 컨테이너 크기는 그대로인 채 공급가만 옮겨가므로, 고정 칼럼 영역 폭도 지켜본다.
    const pinnedLeftHeader = container.querySelector(".ag-pinned-left-header");
    if (pinnedLeftHeader) observer.observe(pinnedLeftHeader);
    // 다운로드 버튼이 생기거나(선택 시) IVA 칸이 붙어 폭이 바뀌어도 다시 잰다.
    if (toolbarLeftRef.current) observer.observe(toolbarLeftRef.current);
    if (toolbarRightRef.current) observer.observe(toolbarRightRef.current);
    if (adjustPriceFragmentRef.current)
      observer.observe(adjustPriceFragmentRef.current);
    return () => observer.disconnect();
  }, [
    isGalleryView,
    selectedProductIds.length,
    isOrderRulesPanelOpen,
    isLoading,
    isAdjustPriceOnOwnRow,
  ]);

  // 삭제/미발행 라인시트세트로 진입해 dense OSS 조회가 404가 되면(=lineSheetSet 없음)
  // 빈 화면+토스트 대신 '존재하지 않는 라인시트' 안내 페이지를 보여준다.
  if (
    getDenseOrderSheetSetError &&
    isLineSheetSetUnavailableError(getDenseOrderSheetSetError)
  ) {
    return <LineSheetNotFoundPage />;
  }

  // 상품을 골랐을 때만 쓰는 것들 — 고르기 전에는 자리만 먹고 눌리지도 않으니 감춘다.
  const adjustPriceFragment = (
    <OrderSheetAdjustPriceFragment
      orderSheetSetId={orderSheetSetId}
      orderSheetId={-1}
      revisionNumber={selectedOrderSheetRevision?.revisionNumber || -1}
      tabIndex={0}
      selectedProductIds={selectedProductIds}
      isOrderSheetSetEditable={isOrderSheetSetEditable}
      commission={getDenseOrderSheetSetData?.lineSheetSet?.commission}
      commissionType={getDenseOrderSheetSetData?.lineSheetSet?.commissionType}
    />
  );

  const downloadSelectedProductsButton = (
    <AppButton
      isLoading={
        isCreateManualExcelExportLoading ||
        isManualExcelExportPolling ||
        isDownloadManualExcelExportFileLoading
      }
      onClick={() => {
        if (!company) return;
        fireCreateManualExcelExport(
          { buyerId: company.id, orderSheetSetId },
          { productIds: selectedProductIds }
        )
          .then(() => setIsManualExcelExportPolling(true))
          .catch((e) => showErrorRef.current(e));
      }}
    >
      {tTitle("download_selected_products")}
    </AppButton>
  );

  // 카드 보기 — 전체 선택 옆에 몇 개 골랐는지까지 함께 보여준다.
  const gallerySelectionActions =
    selectedProductIds.length > 0 ? (
      <Flex alignItems={"center"} gap={"8px"}>
        <Flex
          alignItems={"center"}
          padding={"2px 8px"}
          borderRadius={"999px"}
          background={"#1272EF"}
        >
          <Text
            textStyle={"body-sm-bold"}
            color={"#FFFFFF"}
            whiteSpace={"nowrap"}
          >
            {`${selectedProductIds.length}${t("common:search.selected")}`}
          </Text>
        </Flex>
        {adjustPriceFragment}
      </Flex>
    ) : null;

  return (
    <>
      <Helmet>
        <title>Pathrade | Order Sheets</title>
        <link
          rel="canonical"
          href={getCanonicalUrl()}
        />
        <meta property="og:title" content="Pathrade | Order Sheets" />
        <meta
          property="og:url"
          content={getCanonicalUrl()}
        />
      </Helmet>
      <Flex
        flexDirection={"column"}
        height={"100%"}
        onClick={() => {
          gridRef.current?.api.stopEditing();
        }}
      >
        <Flex
          flexDirection={"row"}
          p={"8px 16px 8px 16px"}
          gap={"12px"}
          alignItems={"center"}
          borderBottom={"0.5px solid var(--gray, #D4D4D1)"}
          background={"var(--white, #FFF)"}
        >
          <Box
            flexShrink={"0"}
            flexGrow={"0"}
            alignItems={"center"}
            display={"flex"}
          >
            <AppIconButton
              variant={"ghost"}
              ariaLabel={"go to previous page"}
              size={"medium"}
              icon={"left"}
              onClick={async () => {
                const result = await confirmDiscardChanges();
                if (query.editable) {
                  setQuery(
                    (prev) => ({
                      ...prev,
                      editable: undefined,
                    }),
                    { replace: true }
                  );
                }
                if (result) {
                  navigate(
                    query.referer
                      ? -1
                      : `/line-sheets?type__eq=${currentLssType}`,
                    {
                      defaultPage: "/line-sheets",
                    }
                  );
                }
              }}
            />
          </Box>
          <Text textStyle={"body-lg-bold"} noOfLines={1}>
            {lineSheetSetName}
          </Text>
          {orderSheetSetStatus &&
            (orderSheetSetStatus === "WORKING_DRAFT" ? (
              <AppOrderSheetStatusLabel value={orderSheetSetStatus} />
            ) : (
              <OrderStatusBadge status={orderSheetSetStatus} />
            ))}
          <Spacer />
          <Flex
            flexDirection={"row"}
            gap={"8px"}
            wrap={"wrap"}
            flexGrow={"0"}
            width={"256px"}
            minWidth={"256px"}
            justifyContent={"end"}
          >
            {company?.type === "BUYER" && (
              <>
                <Flex
                  flexDirection={"row"}
                  gap={"8px"}
                  wrap={"wrap"}
                  flexGrow={"0"}
                >
                  {isOrderSheetResetPossible && (
                    <AppButton
                      width={"80px"}
                      isDisabled={
                        sheetStatisticState?.statistic.totalOrderedQuantity ===
                          0 || !isOrderSheetSetEditable
                      }
                      onClick={() => {
                        gridRef.current?.api.stopEditing(true);
                        if (!inflatedTabs) {
                          return;
                        }
                        setInflatedTabs((prev) => {
                          if (!prev) {
                            return;
                          }
                          return {
                            ...prev,
                            tabs: prev.tabs.map((tab) => {
                              return {
                                ...tab,
                                isDirty: true,
                                products: tab.products.map((product) => {
                                  return {
                                    ...product,
                                    isDirty:
                                      product.orderQuantityWithOptionList.some(
                                        (option) => option.quantity > 0
                                      ),
                                    orderQuantityWithOptionList:
                                      product.orderQuantityWithOptionList.map(
                                        (orderQuantityWithOption) => {
                                          return {
                                            ...orderQuantityWithOption,
                                            quantity: 0,
                                          };
                                        }
                                      ),
                                  };
                                }),
                              };
                            }),
                          };
                        });
                      }}
                    >
                      {tTitle("common:reset_order")}
                    </AppButton>
                  )}
                  {isOrderSheetSetSavable && (
                    <OrderSheetSetSaveButton
                      orderSheetSetId={orderSheetSetId}
                      revisionNumber={
                        selectedOrderSheetRevision?.revisionNumber
                      }
                      isOrderSheetEditable={isOrderSheetSetEditable}
                      isOrderSheetEditing={isOrderSheetEditing}
                      isAllOrderSheetsOrdered={false}
                      isConfirmedEditable={isConfirmedEditable}
                      onUpdatingInflatedProductCompleted={
                        setIsUpdatingInflatedProductCompleted
                      }
                      onOrderSheetSetSaveButtonClicked={
                        setIsOrderSheetSetSaveButtonClicked
                      }
                      onSaveLoading={setIsOrderSheetSaving}
                      onAfterSave={handleAfterSaveOrderSheetSet}
                      gridRef={gridRef}
                      ref={orderSheetSetSaveButtonRef}
                    />
                  )}
                  {isOrderSheetSetOrderable && (
                    <OrderSheetOrderButton
                      isOrderSheetEditing={isOrderSheetEditing}
                      isAllOrderSheetsOrdered={
                        ![
                          "INITIAL_DRAFT",
                          "WORKING_DRAFT",
                          "RESUBMIT_REQUIRED",
                        ].includes(getDenseOrderSheetSetData?.status || "")
                      }
                      isOrderSheetSetOrderable={isOrderSheetSetOrderable}
                      orderSheetSetId={orderSheetSetId}
                      orderedQuantity={totalOrderQuantity}
                      revisionNumber={
                        selectedOrderSheetRevision?.revisionNumber
                      }
                      commission={
                        getDenseOrderSheetSetData?.lineSheetSet.commission
                      }
                      commissionType={
                        getDenseOrderSheetSetData?.lineSheetSet.commissionType
                      }
                      moq={getOrderMoq(getDenseOrderSheetSetData?.lineSheetSet)}
                      canOrderByUnit={canOrderByUnit(
                        getDenseOrderSheetSetData?.lineSheetSet
                      )}
                      hasRevision={
                        getOrderSheetRevisionsData
                          ? getOrderSheetRevisionsData.totalCount !== 0
                          : true
                      }
                      // 최소주문금액 미달 안내에서 패스픽으로 떠나며 저장했을 때 — 저장 버튼과 같은 후처리
                      onAfterDraftSave={handleAfterSaveOrderSheetSet}
                      onUpdatingInflatedProductCompleted={
                        setIsUpdatingInflatedProductCompleted
                      }
                      onOrderSheetOrderButtonClicked={
                        setIsOrderSheetOrderButtonClicked
                      }
                      onSubmit={handleOrderSheetSubmit}
                      gridRef={gridRef}
                      ref={orderSheetOrderButtonRef}
                    />
                  )}
                  {isOrderSheetSetCancelable && !query.editable && (
                    <>
                      <AppButton
                        key={"OrderSheetFormAction_Cancel"}
                        width={"80px"}
                        variant={"white"}
                        isDisabled={!isOrderSheetSetCancelable}
                        // onClick={() => {
                        //   openDialog({
                        //     title: tTitle("common:cancel_order"),
                        //     message: t("cancel_submit.modal.message"),
                        //     confirmName: tTitle("continue"),
                        //   }).then((result) => {
                        //     if (result) {
                        //       fireBulkUpdateOrderSheetSetStatus(
                        //         {
                        //           by: identity!.company!.types,
                        //           companyId: identity!.company!.id,
                        //         },
                        //         {
                        //           status: "CANCELED",
                        //           orderSheetSetIds: [orderSheetSetId],
                        //         }
                        //       );
                        //     }
                        //   });
                        // }}
                        onClick={() => onCancelOpen()}
                      >
                        {tTitle("common:cancel_order")}
                      </AppButton>
                      <AppButton
                        key={"OrderSheetFormAction_Modify"}
                        width={"80px"}
                        variant={"white"}
                        isDisabled={!isOrderSheetSetCancelable}
                        onClick={() => {
                          setQuery(
                            (prev) => ({
                              ...prev,
                              editable: true,
                            }),
                            { replace: true }
                          );
                        }}
                      >
                        오더 수정
                      </AppButton>
                    </>
                  )}
                </Flex>
                {isOnEditEnabledPage && (
                  <OrderSheetOrderButton
                    isOrderSheetEditing={isOrderSheetEditing}
                    isOnEditEnabledPage={isOnEditEnabledPage}
                    isAllOrderSheetsOrdered={false}
                    isOrderSheetSetOrderable={isOnEditEnabledPage}
                    orderSheetSetId={orderSheetSetId}
                    orderedQuantity={totalOrderQuantity}
                    revisionNumber={selectedOrderSheetRevision?.revisionNumber}
                    commission={
                      getDenseOrderSheetSetData?.lineSheetSet.commission
                    }
                    commissionType={
                      getDenseOrderSheetSetData?.lineSheetSet.commissionType
                    }
                    moq={getOrderMoq(getDenseOrderSheetSetData?.lineSheetSet)}
                    canOrderByUnit={canOrderByUnit(
                      getDenseOrderSheetSetData?.lineSheetSet
                    )}
                    hasRevision={
                      getOrderSheetRevisionsData
                        ? getOrderSheetRevisionsData.totalCount !== 0
                        : true
                    }
                    // onSave={handleAfterSaveOrderSheetSet}
                    onUpdatingInflatedProductCompleted={
                      setIsUpdatingInflatedProductCompleted
                    }
                    onOrderSheetOrderButtonClicked={
                      setIsOrderSheetOrderButtonClicked
                    }
                    onSubmit={handleOrderSheetSubmit}
                    gridRef={gridRef}
                    ref={orderSheetOrderButtonRef}
                  />
                )}
                {/*<OrderSheetRevisionSelector*/}
                {/*  minWidth={"100%"}*/}
                {/*  values={orderSheetRevisionSelectValues}*/}
                {/*  value={selectedOrderSheetRevision}*/}
                {/*  onSelect={handleSelectOrderSheetRevision}*/}
                {/*/>*/}
              </>
            )}
          </Flex>
        </Flex>
        <Flex
          paddingX={"16px"}
          paddingY={"12px"}
          background={"#FFFFFF"}
          boxShadow={"0 0 0 0.5px #D4D4D1"}
        >
          {getDenseOrderSheetSetData && (
            <SheetTabFilter
              sheetKey={sheetKey}
              lineSheetSetId={getDenseOrderSheetSetData?.lineSheetSet.id!}
              query={query}
              setQuery={setQuery}
              onFilterSelect={handleFilterSelect}
              onClear={handleSheetTabFilterClear}
              queryCount={queryCount}
              setQueryCount={(count: number) => setQueryCount(count)}
              showQuantityToggle={true}
              likedOnly={likedOnly}
              onLikedOnlyChange={setLikedOnly}
              // 문의/답변 — 제출 전(임시저장) 오더에도 보인다(문의 등록 시 운영 슬랙 알림이 간다)
              rightSlot={
                orderSheetSetId > 0 && (
                  <AppButton variant={"white"} onClick={onInquiryOpen}>
                    {tTitle("order_history:inquiry.section_title")}
                    <InquiryUnreadBadge orderSheetSetId={orderSheetSetId} />
                  </AppButton>
                )
              }
            />
          )}
        </Flex>
        <Flex
          ref={detailBodyRef}
          flexGrow={"1"}
          flexDirection={"column"}
          minHeight={0}
        >
          <Flex justifyContent={"space-between"} position={"relative"}>
            {/* 컨디션 적용은 결국 공급가를 고치는 기능이라 그 컬럼 바로 위에 세운다.
                줄을 새로 만들지 않고 이 줄 안에서 가로 위치만 맞추되, 양쪽 도구와 겹치면 그 사이로 민다. */}
            {!isGalleryView &&
              selectedProductIds.length > 0 &&
              !isAdjustPriceOnOwnRow && (
                <Flex
                  ref={adjustPriceFragmentRef}
                  position={"absolute"}
                  left={`${adjustPriceLeft}px`}
                  top={"50%"}
                  transform={"translateY(-50%)"}
                  width={"max-content"}
                  alignItems={"center"}
                  gap={"8px"}
                  zIndex={1}
                >
                  {adjustPriceFragment}
                </Flex>
              )}
            <Flex
              ref={toolbarLeftRef}
              flexDirection={"row"}
              paddingX={"16px"}
              paddingY={"12px"}
              alignItems={"center"}
              gap={"8px"}
            >
              <AppButton
                variant={"normal"}
                onClick={handleOrderRulesToggle}
                chakraProps={{
                  p: "4px 8px",
                  border: isOrderRulesPanelOpen
                    ? "0.5px solid #00417D"
                    : "0.5px solid #8F8F8C",
                }}
              >
                <Flex gap={"4px"} alignItems={"center"}>
                  <Text
                    textStyle="body-sm-bold"
                    color={isOrderRulesPanelOpen ? "#00417D" : "#444444"}
                  >
                    {tTitle("sheet_info")}
                  </Text>
                  <SVG
                    src={
                      isOrderRulesPanelOpen
                        ? "/icons-v1_1/chevron-double-left.svg"
                        : "/icons-v1_1/chevron-double-right.svg"
                    }
                    width={"16px"}
                    height={"16px"}
                    color={isOrderRulesPanelOpen ? "#00417D" : "#8F8F8C"}
                  />
                </Flex>
              </AppButton>
              {currency !== "KRW" && (
                <>
                  <Divider
                    orientation={"vertical"}
                    marginX={"5px"}
                    borderColor={"#D4D4D1"}
                  />
                  <Flex gap={"8px"} alignItems={"center"}>
                    <Flex gap={"2px"} alignItems={"center"}>
                      <Text textStyle={"body-sm-regular"} color={"#444444"}>
                        {tTitle("currency")}
                      </Text>
                      <Tooltip
                        label={tSentence("floating_bill_board_bar_tooltip")}
                      >
                        <Box>
                          <SVG
                            src={"/icons-v1_1/info-circle.svg"}
                            width={"16px"}
                            height={"16px"}
                          />
                        </Box>
                      </Tooltip>
                    </Flex>
                    <CurrencySwitch
                      checked={isKrw}
                      onChange={(checked: boolean) => setIsKrw(checked)}
                      firstOption={getCurrencySymbol(currency || "EUR")}
                      secondOption={"₩"}
                    />
                  </Flex>
                  <Divider
                    orientation={"vertical"}
                    marginX={"5px"}
                    borderColor={"#D4D4D1"}
                  />
                </>
              )}
              {/* 카드 보기에서만 의미가 있는 것들 — 설정(톱니)에도 같은 값이 있다. */}
              {isGalleryView && (
                <>
                  <ToolbarSwitch
                    id={"expand_all_options"}
                    label={tTitle("option_detail")}
                    isChecked={isAllExpanded}
                    onChange={handleIsAllExpandedChange}
                  />
                  <ToolbarSwitch
                    id={"gallery_show_price_detail_toolbar"}
                    label={tTitle("price_detail")}
                    isChecked={showPriceDetail}
                    onChange={handleShowPriceDetailChange}
                  />
                  {/* 사진 몇 장씩 볼지 — 설정(톱니)과 같은 배치 아이콘이다. */}
                  <Text
                    textStyle={"body-sm-regular"}
                    whiteSpace={"nowrap"}
                    color={"#444444"}
                  >
                    {tTitle("image")}
                  </Text>
                  <Flex gap={"4px"} alignItems={"center"}>
                    {IMAGE_COUNT_OPTIONS.map((count) => (
                      <ImageCountButton
                        key={`image_count_${count}`}
                        count={count}
                        isActive={imageCount === count}
                        onClick={() => setImageCount(count)}
                      />
                    ))}
                  </Flex>
                </>
              )}
            </Flex>

            <Flex
              ref={toolbarRightRef}
              flexDirection={"row"}
              paddingX={"16px"}
              paddingY={"12px"}
              alignItems={"center"}
              gap={"8px"}
            >
              {/* 다운로드끼리 나란히 — 전체 받을지 고른 것만 받을지 한자리에서 고른다. */}
              {selectedProductIds.length > 0 && downloadSelectedProductsButton}
              <AppDownloadButton
                title={tTitle("download_all")}
                value={downloadFile}
              />
              <AppSelect<string>
                backgroundColor={"transparent"}
                borderWidth={"0px"}
                isMultiple={false}
                isClearable={false}
                name={tTitle("sort_by")}
                value={query.orderBy}
                options={
                  [
                    { name: tTitle("default"), value: "default" },
                    { name: tTitle("price_asc"), value: "price_asc" },
                    { name: tTitle("price_desc"), value: "price_desc" },
                  ] as AppSelectOption<string>[]
                }
                onSelect={(value) =>
                  setQuery(
                    (prev) => {
                      return {
                        ...prev,
                        orderBy: value,
                      };
                    },
                    {
                      replace: true,
                    }
                  )
                }
              />
              {/* 카드 보기(갤러리) / 자세히 보기(편집 그리드) 전환 */}
              <Flex gap={"4px"} alignItems={"center"}>
                {/* 보기를 바꿔 본 적 없는 사용자에게 최초 1회 띄우는 안내 — 두 버튼을 가리킨다 */}
                <Popover
                  isOpen={isViewTipOpen}
                  onClose={closeViewTip}
                  placement={"bottom-end"}
                  closeOnBlur={false}
                  closeOnEsc={true}
                >
                  <PopoverTrigger>
                    <Flex gap={"4px"} alignItems={"center"}>
                      <ViewModeButton
                        isActive={isGalleryView}
                        label={tTitle("card_view")}
                        onClick={() => handleViewChange(true)}
                      >
                        <path d="M3 3h7v7H3V3zm11 0h7v7h-7V3zM3 14h7v7H3v-7zm11 0h7v7h-7v-7z" />
                      </ViewModeButton>
                      <ViewModeButton
                        isActive={!isGalleryView}
                        label={tTitle("detail_view")}
                        onClick={() => handleViewChange(false)}
                      >
                        <path d="M3 5h18v2H3V5zm0 6h18v2H3v-2zm0 6h18v2H3v-2z" />
                      </ViewModeButton>
                    </Flex>
                  </PopoverTrigger>
                  <PopoverContent
                    width={"280px"}
                    background={"#1272EF"}
                    color={"#FFFFFF"}
                    borderColor={"#1272EF"}
                    zIndex={10}
                  >
                    <PopoverArrow background={"#1272EF"} />
                    <PopoverBody
                      display={"flex"}
                      flexDirection={"column"}
                      gap={"8px"}
                      padding={"12px"}
                    >
                      <Text fontSize={"13px"} fontWeight={"700"}>
                        {t("order_sheet_view_tip.title")}
                      </Text>
                      <Text fontSize={"12px"} lineHeight={"1.5"}>
                        {t("order_sheet_view_tip.body")}
                      </Text>
                      <Flex justifyContent={"flex-end"}>
                        <Button
                          size={"xs"}
                          background={"#FFFFFF"}
                          color={"#1272EF"}
                          _hover={{ background: "#F0F9FF" }}
                          onClick={closeViewTip}
                        >
                          {t("order_sheet_view_tip.confirm")}
                        </Button>
                      </Flex>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
                {/* 카드 보기에서 무엇을 보여줄지는 톱니 뒤 설정에서 고른다. */}
                {isGalleryView && (
                  <ViewModeButton
                    isActive={false}
                    label={tTitle("card_view_settings")}
                    onClick={onGallerySettingsOpen}
                  >
                    <path d="M19.14 12.94a7.07 7.07 0 000-1.88l2.03-1.58a.5.5 0 00.12-.64l-1.92-3.32a.5.5 0 00-.61-.22l-2.39.96a7.03 7.03 0 00-1.62-.94l-.36-2.54a.5.5 0 00-.5-.42h-3.84a.5.5 0 00-.5.42l-.36 2.54c-.58.24-1.12.55-1.62.94l-2.39-.96a.5.5 0 00-.61.22L2.65 8.84a.5.5 0 00.12.64l2.03 1.58a7.07 7.07 0 000 1.88l-2.03 1.58a.5.5 0 00-.12.64l1.92 3.32c.13.22.39.31.61.22l2.39-.96c.5.39 1.04.7 1.62.94l.36 2.54c.04.24.25.42.5.42h3.84c.25 0 .46-.18.5-.42l.36-2.54c.58-.24 1.12-.55 1.62-.94l2.39.96c.22.09.48 0 .61-.22l1.92-3.32a.5.5 0 00-.12-.64l-2.03-1.58zM12 15.6A3.6 3.6 0 1112 8.4a3.6 3.6 0 010 7.2z" />
                  </ViewModeButton>
                )}
              </Flex>
            </Flex>
          </Flex>
          {/* 도구 사이에 들어갈 자리가 없을 때만 — 컨디션 적용을 아랫줄로 내려 다운로드 버튼과 겹치지 않게 한다. */}
          {!isGalleryView &&
            selectedProductIds.length > 0 &&
            isAdjustPriceOnOwnRow && (
              <Flex paddingX={"16px"} paddingBottom={"8px"}>
                <Flex
                  ref={adjustPriceFragmentRef}
                  width={"max-content"}
                  alignItems={"center"}
                  gap={"8px"}
                >
                  {adjustPriceFragment}
                </Flex>
              </Flex>
            )}
          <Flex flexGrow={1} flexDirection={"row"} gap={"8px"} minHeight={0}>
            {isGalleryView ? (
              <>
                {/* 시트 정보 패널은 그리드 컴포넌트 안에 있어서 갤러리에서는 안 보였다.
                    갤러리에서도 같은 패널을 왼쪽에 붙여 준다. */}
                {isOrderRulesPanelOpen && (
                  <Box flexShrink={0}>
                    <SheetInfoPanel
                      onClose={handleOrderRulesToggle}
                      showMoqNotice
                      lineSheetSet={getDenseOrderSheetSetData?.lineSheetSet}
                      orderSheetSet={
                        getDenseOrderSheetSetData as unknown as OrderSheetSet
                      }
                    />
                  </Box>
                )}
                <OrderSheetProductGallery
                  sheetKey={sheetKey}
                  tabIndex={0}
                  isLoading={isLoading}
                  isEditable={isOrderSheetSetEditable}
                  priceConfig={{
                    commission:
                      getDenseOrderSheetSetData?.lineSheetSet?.commission,
                    commissionType:
                      getDenseOrderSheetSetData?.lineSheetSet?.commissionType,
                  }}
                  onUpdatingInflatedProductComplete={
                    setIsUpdatingInflatedProductCompleted
                  }
                  selectedProductIds={selectedProductIds}
                  onSelectedProductIdsChange={setSelectedProductIds}
                  selectionActions={gallerySelectionActions}
                  imageCount={imageCount}
                  showPriceDetail={showPriceDetail}
                  isAllExpanded={isAllExpanded}
                  likedOnly={likedOnly}
                  currency={getDenseOrderSheetSetData?.lineSheetSet?.currency}
                  marketPriceByProductId={
                    isMarketPriceEnabled ? marketPriceByProductId : undefined
                  }
                  marketPricing={marketPricing}
                  marketComparisonLayout={marketComparisonLayout}
                />
              </>
            ) : (
              <BuyerOrderSheet
                isLoading={isLoading}
                sheetKey={sheetKey}
                marketPriceByProductId={
                  isMarketPriceEnabled ? marketPriceByProductId : undefined
                }
                marketPricing={marketPricing}
                lineSheetSetId={
                  getDenseOrderSheetSetData?.lineSheetSet.id || -1
                }
                tabIndex={0}
                isOrderEditable={isOrderSheetSetEditable}
                isConfirmedEditable={isConfirmedEditable}
                isConditionEditable={isConditionEditable}
                onEditing={handleOrderSheetSetEditing}
                onSelectedProductIdsChange={setSelectedProductIds}
                onGridReady={handleGridReady}
                likedOnly={likedOnly}
                onUpdatingInflatedProductComplete={
                  setIsUpdatingInflatedProductCompleted
                }
                orderSheetSet={getDenseOrderSheetSetData}
                isOrderRulesPanelOpen={isOrderRulesPanelOpen}
                getDenseOrderSheetSetData={getDenseOrderSheetSetData}
                handleOrderRulesSave={handleOrderRulesSave}
                handleOrderRulesToggle={handleOrderRulesToggle}
                // revisionNumber={selectedOrderSheetRevision?.revisionNumber}
              />
            )}
          </Flex>
        </Flex>
      </Flex>
      <OrderSheetGallerySettingsModal
        isOpen={isGallerySettingsOpen}
        onClose={onGallerySettingsClose}
        imageCount={imageCount}
        onImageCountChange={setImageCount}
        showPriceDetail={showPriceDetail}
        onShowPriceDetailChange={handleShowPriceDetailChange}
        isAllExpanded={isAllExpanded}
        onIsAllExpandedChange={handleIsAllExpandedChange}
        marketComparisonLayout={
          isMarketPriceEnabled ? marketComparisonLayout : undefined
        }
        onMarketComparisonLayoutChange={handleMarketComparisonLayoutChange}
      />
      <OrderHistoryOrderInfoOrderCancelModal
        isOpen={isCancelOpen}
        onClose={onCancelClose}
        onConfirm={() => {
          if (!orderSheetSetId) return;
          fireBulkUpdateOrderSheetSetStatus(
            {
              by: identity!.company!.type,
              companyId: identity!.company!.id,
            },
            {
              status: "CANCELED",
              orderSheetSetIds: [orderSheetSetId],
            }
          );
          onCancelClose();
        }}
      />
      <Drawer
        isOpen={isInquiryOpen}
        onClose={onInquiryClose}
        placement={"right"}
        size={"md"}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader textStyle={"body-lg-bold"}>
            {tTitle("order_history:inquiry.section_title")}
          </DrawerHeader>
          <DrawerBody display={"flex"} flexDirection={"column"} minHeight={0}>
            {isInquiryOpen && orderSheetSetId > 0 && (
              <React.Suspense fallback={<CenteredSpinner />}>
                <OrderHistoryInquiryContent
                  orderSheetSetId={orderSheetSetId}
                  fillHeight={true}
                />
              </React.Suspense>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}

// BuyerOrderSheetSetPage.whyDidYouRender = true;

export default BuyerOrderSheetSetPage;
